/**
 * 多段ヘッダーのレイアウト計算。
 *
 * 表で使われている条件分岐の最大の深さ D に対して、ヘッダーは D+1 段になる(分岐が無ければ 1 段)。
 * - 深さ k の条件分岐は、配下の列すべてをまとめた帯を k 段目(0 始まり)に置く
 * - 列見出しは「自分の深さ」の段に置き、最下段まで rowSpan で伸ばす(条件を持たない列は 0 段目から)
 *
 *   列0 | 列1 |  「はい」を選択した場合(帯)          | 列2
 *      |     |  分岐質問0 | 「有」を選択した場合(帯)  |
 *      |     |            |  分岐質問0-0            |
 */

import type { TableDisplayColumn } from './tableDisplayColumn'

/** 条件を表示する帯1本。1つの条件分岐の配下をまとめる。 */
export type TableHeaderBandCell = {
  kind: 'band'
  logicId: number
  label: string
  /** この条件分岐を持つ親質問の ID。削除時に条件分岐の参照を解除する対象。 */
  parentQuestionId: number
  colSpan: number
  /** 分岐質問を1つ以上持つか(削除確認の文言出し分け用)。 */
  hasBranchQuestion: boolean
}

/** 列見出し1つ。自分の段から最下段まで縦に伸ばす。 */
export type TableHeaderLeafCell = {
  kind: 'leaf'
  column: TableDisplayColumn
  rowSpan: number
}

export type TableHeaderCell = TableHeaderBandCell | TableHeaderLeafCell

const conditionsOf = (column: TableDisplayColumn) =>
  column.kind === 'branch' ? column.conditions : []

/**
 * 表示される列から D+1 段のヘッダーを組み立てる。
 *
 * 同じ帯にまとめるのは「深さ k が同じ条件分岐である列が連続する区間」。
 * 1つの質問が同じ文言の条件分岐を複数持つこともあるため、文言ではなく id で判定する。
 */
export const getTableHeaderRows = (
  displayColumns: TableDisplayColumn[],
): TableHeaderCell[][] => {
  const maxDepth = Math.max(
    0,
    ...displayColumns.map(column => conditionsOf(column).length),
  )

  // 段ごとに左から 1 度だけ走査する。各列はその段で「帯の一部」「自分の見出し」
  // 「上の段から伸びてきた見出しに覆われている」のいずれかになる。
  const buildRow = (depth: number): TableHeaderCell[] => {
    const row: TableHeaderCell[] = []
    let index = 0
    while (index < displayColumns.length) {
      const column = displayColumns[index]!
      const leaf: TableHeaderCell = {
        kind: 'leaf',
        column,
        rowSpan: maxDepth + 1 - depth,
      }

      if (column.kind === 'column') {
        // 条件を持たない列は 0 段目に置き、最下段まで伸ばす。
        if (depth === 0) row.push(leaf)
        index += 1
        continue
      }

      if (column.conditions.length === depth) {
        row.push(leaf)
        index += 1
        continue
      }

      const condition = column.conditions[depth]
      if (!condition) {
        // 上の段に置いた見出しが rowSpan で伸びてきている。この段には何も置かない。
        index += 1
        continue
      }

      let end = index + 1
      while (
        end < displayColumns.length &&
        conditionsOf(displayColumns[end]!)[depth]?.logicId === condition.logicId
      ) {
        end += 1
      }
      // 区間の先頭は必ずこの条件分岐の直下に並ぶ分岐列。その親質問が分岐元になる。
      row.push({
        kind: 'band',
        logicId: condition.logicId,
        label: condition.label,
        parentQuestionId: column.parentQuestionId,
        colSpan: end - index,
        hasBranchQuestion: column.question !== undefined,
      })
      index = end
    }
    return row
  }

  return Array.from({ length: maxDepth + 1 }, (_, depth) => buildRow(depth))
}
